import type { OnboardingStep } from '@contracts/persona';

/**
 * The wizard's words (S8 §8.1, mockup Màn 1). Step 1 is the forced change-password page, shown here as done:
 * whoever reaches `/welcome` has already passed it (or never had a temporary password).
 */
export const SECURITY_STEP = 'Bảo mật';
export const STEP_TITLE: Record<OnboardingStep, string> = {
  2: 'Về bạn',
  3: 'Trợ lý của bạn',
  4: 'Nhịp làm việc',
  5: 'Kết nối',
  6: 'Lời chào đầu tiên',
};
export const TOTAL_STEPS = 6;

/** S8 D128: the steps' short names, as the bell reminder lists them (mockup Màn 2). */
export const STEP_SHORT: Record<OnboardingStep, string> = {
  2: 'Về bạn',
  3: 'Trợ lý của bạn',
  4: 'Nhịp làm việc',
  5: 'Kết nối',
  6: 'Lời chào',
};

/** S8 D128 (Q4 ★): about two thirds of a minute a step, never under one. */
export const reminderMinutes = (steps: number): number => Math.max(1, Math.ceil((steps * 2) / 3));

/** S8 D128: the bell reminder, mockup Màn 2. It names no persona: the reader may not have chosen one yet. */
export const REMINDER = {
  title: 'Hoàn tất cài đặt trợ lý',
  body: (remaining: readonly OnboardingStep[]) => `Còn ${remaining.length} bước: ${
    remaining.map((s) => STEP_SHORT[s]).join(', ')}. Khoảng ${reminderMinutes(remaining.length)} phút.`,
  resume: (step: OnboardingStep) => `Tiếp tục ở bước ${step}`,
} as const;
export const WELCOME = {
  steps: 'Các bước hướng dẫn',
  done: 'đã xong',
  stepOf: (k: number) => `Bước ${k}/${TOTAL_STEPS}`,
  back: 'Quay lại',
  later: 'Để sau',
  next: 'Tiếp tục',
  finish: 'Bắt đầu làm việc',
  keepAll: 'Giữ nguyên tất cả',
  loading: 'Đang tải…',
  meFailed: 'Chưa tải được phần hướng dẫn.',
  enterApp: 'Vào ứng dụng',
} as const;

/** Step 2 "Về bạn" (mockup Màn 1 step 2). */
export const ABOUT = {
  name: 'Tên hiển thị',
  nameRequired: 'Bắt buộc nhập',
  willCall: 'Trợ lý sẽ gọi:',
  /** The existing-person notice; "anh" in the mockup is the person, so it follows how they asked to be called. */
  existing: (you: string) => `Trợ lý vừa có thêm cài đặt cá nhân. Mọi ô đã điền sẵn giá trị ${you} đang dùng.`,
} as const;

/** Step 3 "Trợ lý của bạn" (mockup Màn 1 step 3). */
export const ASSISTANT_STEP = {
  look: 'Ngoại hình',
  names: 'Tên và cách xưng',
  selfName: 'Trợ lý tự xưng',
  personality: 'Tính cách',
  instructions: 'Lời dặn riêng',
  optional: 'tuỳ chọn',
  placeholder: (you: string) => `Điều ${you} muốn trợ lý luôn nhớ khi làm việc với ${you}`,
  examples: 'Bấm một ví dụ để chèn',
} as const;

/** Step 6 "Lời chào đầu tiên" (mockup Màn 1 step 6): the commands' group, named for a screen reader. */
export const GREETING_STEP = {
  tryNow: 'Thử ngay',
} as const;

/** Step 4 "Nhịp làm việc" (mockup Màn 1 step 4; D135). The weekly time is the server's one for everyone (S3 §8.1). */
export const RHYTHM_STEP = {
  briefingTime: 'Giờ gửi briefing sáng',
  weekly: 'Bản tổng kết tuần',
  weeklyWhen: (time: string) => `Chiều thứ 6, ${time}`,
  quiet: 'Giờ yên lặng',
  from: 'Từ',
  to: 'Đến',
  weekend: 'Yên lặng cả thứ 7, chủ nhật',
  risk: 'Nhắc rủi ro',
  riskOn: 'Bật',
  riskOnHint: 'Nhắc việc trễ, việc bị chặn, rủi ro',
  riskOwn: 'Chỉ lịch nhắc tôi tự đặt',
  riskOwnHint: 'Tắt mọi nhắc tự động',
  nudgesOff: 'Công ty đang tắt nhắc việc',
  briefingsOff: 'Công ty đang tắt bản tóm tắt',
  loadWhat: 'nhịp làm việc của bạn',
  loading: 'Đang tải…',
} as const;

/** Step 5 "Kết nối" (mockup Màn 1 step 5): every connection is optional, and so is the step. */
export const CONNECT_STEP = {
  lead: 'Tất cả đều tuỳ chọn, có thể kết nối sau ở Trợ lý của tôi.',
  empty: 'Chưa có kết nối nào được bật. Bạn có thể bỏ qua bước này.',
  returnTo: '/welcome#connections',
} as const;
